import React from "react";
import {
	CloudUpload as Upload,
	AutoFixHigh as Wand2,
	Close as X,
	Autorenew as Loader2,
	AutoAwesome as Sparkles,
} from "@mui/icons-material";
import { FEATURE_CONFIG,FEATURE_ICONS } from "../../../../utils/imageEditor";
import styles from "../AiImageGenerator.module.css";
import FeatureOptionsPanel from "./FeatureOptionsPanel";
import ErrorBanner from "./ErrorBanner";

export function EnhancePanel({
	error,
	imageFile,
	previewUrl,
	fileInputRef,
	isDragOver,
	selectedFeatures,
	featureOptions,
	expandAiBackgroundConflictNote,
	loading,
	applyDisabled,
	onFileChange,
	onDrop,
	setIsDragOver,
	clearImage,
	toggleFeature,
	setOption,
	onApply,
	features,
}) {
	return (
		<>
			<div className={styles.panelCard}>
				<h2 className={styles.panelHeading}>
					<Upload sx={{ fontSize: 16, color: "var(--aig-dark)" }} />
					Upload Image
				</h2>

				{!imageFile ? (
					<div
						className={`${styles.uploadDrop} ${isDragOver ? styles.uploadDropActive : ""}`}
						onClick={() => fileInputRef.current?.click()}
						onDragOver={(e) => {
							e.preventDefault();
							setIsDragOver(true);
						}}
						onDragLeave={() => setIsDragOver(false)}
						onDrop={onDrop}
					>
						<Upload
							sx={{
								fontSize: 40,
								color: "var(--aig-dark)",
								display: "block",
								margin: "0 auto 12px",
							}}
						/>
						<p className={styles.uploadDropTitle}>
							Drag & drop or click to upload
						</p>
						<p className={styles.uploadDropSub}>
							PNG, JPG, WEBP — max 10 MB
						</p>
					</div>
				) : (
					<div className={styles.uploadPreviewWrap}>
						<div className={styles.uploadPreviewBox}>
							<img
								src={previewUrl}
								alt="Preview"
								className={styles.uploadPreviewImg}
							/>
						</div>
						<button onClick={clearImage} className={styles.uploadPreviewRemove}>
							<X sx={{ fontSize: 16 }} />
						</button>
						<p className={styles.uploadPreviewMeta}>
							{imageFile.name} ({(imageFile.size / 1024).toFixed(0)} KB)
						</p>
					</div>
				)}
				<input
					ref={fileInputRef}
					type="file"
					accept="image/*"
					className={styles.hiddenInput}
					onChange={onFileChange}
				/>
			</div>

			<div className={styles.panelCard}>
				<h2 className={styles.panelHeading}>
					<Wand2 sx={{ fontSize: 16, color: "var(--aig-dark)" }} />
					Select Features
				</h2>
				<p className={styles.featureNote}>
					All selected features in one request. Same request is sent 3 times;
					results in 2×2 grid.
				</p>

				{expandAiBackgroundConflictNote && (
					<p className={styles.conflictNote}>
						AI Expand cannot be used with AI Background or AI Shadow. Please use
						one at a time.
					</p>
				)}

				<div className={styles.featureList}>
					{features.map((f) => {
						const Icon = FEATURE_ICONS[f.id];
						const isSelected = selectedFeatures.includes(f.id);
						const orderIndex = selectedFeatures.indexOf(f.id) + 1;
						const looksDisabledByConflict =
							(f.id === "expand" &&
								(selectedFeatures.includes("aiBackground") || selectedFeatures.includes("shadow"))) ||
							((f.id === "aiBackground" || f.id === "shadow") &&
								selectedFeatures.includes("expand"));

						return (
							<button
								key={f.id}
								type="button"
								onClick={() => toggleFeature(f.id)}
								className={[
									styles.featureBtn,
									isSelected ? styles.featureBtnSelected : "",
									looksDisabledByConflict ? styles.featureBtnConflict : "",
								].join(" ")}
							>
								<span
									className={`${styles.featureBadge} ${isSelected ? styles.featureBadgeSelected : ""}`}
								>
									{isSelected ? orderIndex : ""}
								</span>
								<Icon
									sx={{
										fontSize: 20,
										flexShrink: 0,
										color: isSelected ? "#ffffff" : "var(--aig-dark)",
									}}
								/>
								<div className={styles.featureText}>
									<p className={styles.featureLabel}>{f.label}</p>
									<p
										className={`${styles.featureDesc} ${isSelected ? styles.featureDescSelected : ""}`}
									>
										{f.description}
									</p>
								</div>
							</button>
						);
					})}
				</div>
			</div>

			<div className={styles.panelCard}>
				<h2 className={styles.panelHeading}>
					<Sparkles sx={{ fontSize: 16, color: "var(--aig-dark)" }} />
					Options
				</h2>
				{selectedFeatures.length === 0 ? (
					<p className={styles.noOptionsText}>
						Select at least one feature above.
					</p>
				) : (
					<div className={styles.featureOptionsGroup}>
						{selectedFeatures.map((featureId, idx) => (
							<div key={featureId} className={styles.featureOptionStep}>
								<p className={styles.stepLabel}>
									Step {idx + 1}: {FEATURE_CONFIG[featureId].label}
								</p>
								<FeatureOptionsPanel
									feature={featureId}
									options={featureOptions}
									setOption={setOption}
								/>
							</div>
						))}
					</div>
				)}
			</div>
			{error && <ErrorBanner message={error} />}
			<button
				onClick={onApply}
				disabled={applyDisabled}
				className={styles.primaryBtn}
			>
				{loading ? (
					<>
						<Loader2 sx={{ fontSize: 20 }} className={styles.spin} />
						Applying {selectedFeatures.length} feature
						{selectedFeatures.length !== 1 ? "s" : ""}…
					</>
				) : (
					<>
						<Wand2 sx={{ fontSize: 20 }} />
						{selectedFeatures.length === 1
							? "Apply Feature"
							: `Apply ${selectedFeatures.length} Features`}
					</>
				)}
			</button>
		</>
	);
}
